import { subDays, subHours } from 'date-fns'

import { EMPTY_OBJECT } from '../../../../constants/common'
import {
  ASSIGNEE_FILTER_NAME,
  DATE_RANGE_FILTER_TODAY,
  DUE_DATE_FILTER_NAME,
  PRIVATE_UPDATED_FILTER_NAME,
  STATUS_FILTER_NAME,
} from '../../../../constants/filter'
import {
  REQUEST_IN_PROGRESS_STATUS,
  REQUEST_PENDING_STATUS,
} from '../../../../constants/request'
import {
  getDateEndParam,
  getDateRangeParam,
  getDateStartParam,
} from '../../../../utils/filter'
import {
  BIRDVIEW_ATTENTION_GROUPS,
  BIRDVIEW_ATTENTION_NO_ACTIVITY_HOURS,
  BirdviewAttentionGroup,
} from '../../DashboardBirdview.constants.attention'
import { checkIsBirdviewAttentionDefaultAge } from '../../DashboardBirdview.utils'
import {
  getBirdviewActiveRequestsFilters,
  getBirdviewNoProviderRequestsFilters,
  getBirdviewOpenRequestsFilters,
  getBirdviewOverdueRequestsFilters,
} from '../../DashboardBirdview.utils.api'

// A bucket of N..M days old = the field falls between now − M and now − N.
function getBirdviewAttentionAgeFilters({ age, field }) {
  if (checkIsBirdviewAttentionDefaultAge(age)) {
    return EMPTY_OBJECT
  }

  const now = new Date()

  return {
    [getDateEndParam(field)]: subDays(now, age.minDays).toISOString(),
    ...(age.maxDays
      ? { [getDateStartParam(field)]: subDays(now, age.maxDays).toISOString() }
      : EMPTY_OBJECT),
  }
}

function getBirdviewAttentionGroupBaseFilters({ age, group, scope, userId }) {
  const params = { scope, userId }

  switch (group) {
    case BirdviewAttentionGroup.DueToday:
      return {
        ...getBirdviewOpenRequestsFilters(params),
        [getDateRangeParam(DUE_DATE_FILTER_NAME)]: [DATE_RANGE_FILTER_TODAY],
      }
    // The backend drops due_date_gte / lte next to due_date_range, so an age
    // bucket bounds the due date itself: an upper bound in the past is overdue.
    case BirdviewAttentionGroup.Overdue:
      return checkIsBirdviewAttentionDefaultAge(age)
        ? getBirdviewOverdueRequestsFilters(params)
        : getBirdviewOpenRequestsFilters(params)
    case BirdviewAttentionGroup.Pending:
      return {
        ...getBirdviewActiveRequestsFilters(params),
        [STATUS_FILTER_NAME]: [REQUEST_PENDING_STATUS],
      }
    case BirdviewAttentionGroup.InProgress:
      return {
        ...getBirdviewActiveRequestsFilters(params),
        [STATUS_FILTER_NAME]: [REQUEST_IN_PROGRESS_STATUS],
      }
    case BirdviewAttentionGroup.Unassigned:
      return {
        ...getBirdviewOpenRequestsFilters(params),
        [ASSIGNEE_FILTER_NAME]: [null],
      }
    case BirdviewAttentionGroup.NoActivity:
      return {
        ...getBirdviewOpenRequestsFilters(params),
        [getDateEndParam(PRIVATE_UPDATED_FILTER_NAME)]: subHours(
          new Date(),
          BIRDVIEW_ATTENTION_NO_ACTIVITY_HOURS
        ).toISOString(),
      }
    default:
      return getBirdviewNoProviderRequestsFilters(params)
  }
}

function getBirdviewAttentionGroupDefinition(group) {
  return BIRDVIEW_ATTENTION_GROUPS.find(item => item.key === group)
}

export function getBirdviewAttentionGroupFilters({
  age,
  group,
  scope,
  userId,
}) {
  return {
    ...getBirdviewAttentionGroupBaseFilters({ age, group, scope, userId }),
    ...getBirdviewAttentionAgeFilters({
      age,
      field: getBirdviewAttentionGroupDefinition(group).ageField,
    }),
  }
}

export function toBirdviewAttentionSection(groupSections) {
  const items = BIRDVIEW_ATTENTION_GROUPS.map((group, index) => ({
    ...group,
    count: groupSections[index].total,
    extra: groupSections[index].extra,
    id: group.key,
    items: groupSections[index].items,
  })).filter(group => group.count > 0)

  return {
    items,
    total: items.reduce((sum, group) => sum + group.count, 0),
  }
}
